import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { showToast } from '../config_and_helpers/helpers';
import { SupplierCache } from '../config_and_helpers/supplier_cache';
import { logError } from '../config_and_helpers/logger';
import { isCanceledRequest } from '../utils/listSearchRequest';

/**
 * Creates a new supplier.
 *
 * This function sends a POST request to the backend API to create a new supplier using the supplierData parameter.
 * If the request is successful, it returns an object with the keys 'success' (true) and 'toast' (a function that shows a success toast).
 * If any step fails, it returns an array of error messages.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Object} supplierData - The data for the new supplier.
 * @returns {Promise<Object>} - An object with a boolean 'success' attribute and a 'toast' function. For unsuccessful actions, it returns an array of error messages.
 * @throws Will throw an error if the create supplier request fails.
 */
export const createSupplier = async (token, supplierData) => {
  // Try to send a POST request to create a new supplier with the supplied data
  try {
    await axios.post(`${BACKEND_URL}suppliers/`, supplierData, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    // If the request is successful, return an object with success set to true and a toast function
    // which shows a success toast when the supplier has been created
    return {
      success: true,
      toast: () =>
        showToast(
          'Supplier created successfully!',
          'success',
          'top-right',
          3000
        ),
    };

    // If any error occurs during the creation of the supplier
  } catch (error) {
    logError('supplier_client', 'createSupplier failed', error.response?.data ?? error);
    if (error.response?.data) {
      return Object.values(error.response.data);
    }
    return [error.message];
  }
};

/**
 * Update a supplier.
 *
 * This function sends a PATCH request to the backend API to update an existing supplier using the supplierId and updatedSupplierData.
 * The function also calls the provided setSupplier callback function to update the supplier state with the updated supplier data.
 * If the request is successful, it returns an object with the keys 'success' (true) and 'toast' (a function that shows a success toast).
 * If any step fails, it returns an array of error messages.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} supplierId - The ID of the supplier to update.
 * @param {Object} updatedSupplierData - The updated data for the supplier.
 * @param {function} setSupplier - The function to update the supplier state.
 * @returns {Promise<Object>} - An object with a boolean 'success' attribute and a 'toast' function. For unsuccessful actions, it returns an array of error messages.
 * @throws Will throw an error if the update supplier request fails.
 */
export const updateSupplier = async (
  token,
  supplierId,
  updatedSupplierData,
  setSupplier
) => {
  // Try to send a PATCH request to update a specific supplier using the provided supplierId and updated data
  try {
    const response = await axios.patch(
      `${BACKEND_URL}suppliers/${supplierId}/`,
      updatedSupplierData,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    // If the request is successful and the setSupplier callback function is provided
    if (setSupplier) {
      // Use the setSupplier function to update the supplier state with the updated supplier data
      // returned from the PATCH request
      setSupplier(response.data);
    }

    return {
      success: true,
      updatedSupplier: response.data,
      toast: () =>
        showToast(
          'Supplier updated successfully!',
          'success',
          'top-right',
          3000
        ),
    };

    // If any error occurs during the update of the supplier
  } catch (error) {
    logError('supplier_client', 'updateSupplier failed', error.response?.data ?? error);
    if (error.response?.data) {
      return Object.values(error.response.data);
    }
    return [error.message];
  }
};

/**
 * Deletes a supplier.
 *
 * This function sends a DELETE request to the backend API to delete an existing supplier using the supplierId.
 * If the request is successful, it returns an object with the keys 'success' (true) and 'toast' (a function that shows a success toast).
 * If any step fails, it returns an array of error messages.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} supplierId - The ID of the supplier to delete.
 * @returns {Promise<Object>} - An object with a boolean 'success' attribute and a 'toast' function. For unsuccessful actions, it returns an array of error messages.
 * @throws Will throw an error if the delete supplier request fails.
 */
export const deleteSupplier = async (token, supplierId) => {
  // Try to send a DELETE request to delete a specific supplier using the provided supplierId
  try {
    await axios.delete(`${BACKEND_URL}suppliers/${supplierId}/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    // If the request is successful, return an object with success set to true and a toast function
    // which shows a success toast when the supplier has been deleted
    return {
      success: true,
      toast: () =>
        showToast(
          'Supplier deleted successfully!',
          'success',
          'top-right',
          3000
        ),
    };

    // If any error occurs during the deletion of the supplier
  } catch (error) {
    logError('supplier_client', 'deleteSupplier failed', error.response?.data ?? error);
    if (error.response?.data) {
      return Object.values(error.response.data);
    }
    return [error.message];
  }
};

/**
 * Retrieve a list of suppliers.
 *
 * This function sends a GET request to the backend API to retrieve a list of suppliers. It accepts a token,
 * a callback function for setting suppliers, and an options object as arguments.
 * The options object can include a 'searchInput' for custom queries and a 'nextPage' URL for pagination.
 * The response from the API is then used to update the suppliers.
 * If there is a next page of results, the function returns an object with 'success' (true) and 'nextPage' (the URL of the next page) keys.
 * If there are no more results, it returns an object with 'success' (true) and 'reachedEnd' (true) keys.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {function} setSuppliers - The function to set suppliers state.
 * @param {Object} options - The options object. It can contain 'searchInput' for custom queries and 'nextPage' for pagination.
 * @returns {Promise<Object>} - An object with 'success', 'nextPage' or 'reachedEnd' keys depending on the situation.
 * @throws Will throw an error if the get suppliers request fails.
 */
export const getSuppliers = async (token, setSuppliers, options = {}) => {
  // Default the searchInput and nextPage to empty string and null if not provided in options
  const {
    searchInput = '',
    manufacturerId = '',
    nextPage = null,
    sortBy = '',
    sortDirection = '',
    signal = undefined,
  } = options;

  // Use backend-provided next-page URLs as-is to avoid query drift between pages.
  let url = nextPage ? nextPage : `${BACKEND_URL}suppliers/?page_num=1`;

  // Only append filters/sort on first-page requests.
  if (!nextPage) {
    if (searchInput) {
      url += `&search=${searchInput}`;
    }
    if (manufacturerId) {
      url += `&manufacturer_id=${manufacturerId}`;
    }
    if (sortBy) {
      url += `&sort_by=${sortBy}`;
    }
    if (sortDirection) {
      url += `&sort_direction=${sortDirection}`;
    }
  }

  // Try to send a GET request to retrieve the list of suppliers
  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Token ${token}`,
      },
      signal,
    });

    // Extract the next page URL from the response
    const nextCursor = response.data.next;
    // If there is no next page
    if (!nextCursor) {
      if (nextPage) {
        // If it's not the first page, append the new results to the previous ones
        setSuppliers(prevSuppliers => [
          ...prevSuppliers,
          ...response.data.results,
        ]);
      } else {
        // If it's the first page, replace the old results
        setSuppliers(response.data.results);
      }
      // Indicate success and the end of pagination
      return { success: true, reachedEnd: true };
    }

    // If there is a next page...
    if (!nextPage) {
      // If it's the first page, set the suppliers list to the obtained results
      setSuppliers(response.data.results);
    } else {
      // If it's not the first page, append the new results to the previous ones
      setSuppliers(prevSuppliers => [
        ...prevSuppliers,
        ...response.data.results,
      ]);
    }

    // Indicate success and return the next page URL
    return { success: true, nextPage: response.data.next };

    // If any error occurs during the retrieval of the suppliers
  } catch (error) {
    if (isCanceledRequest(error)) {
      return { canceled: true };
    }
    logError('supplier_client', 'getSuppliers failed', error.response?.data ?? error);
    if (error.response?.data) {
      return Object.values(error.response.data);
    }
    return [error.message];
  }
};

/**
 * Retrieves the supplier select list from the backend API.
 *
 * @async
 * @param {string} token - The authentication token for the API.
 * @param {Function} setSuppliers - The function to update the state of suppliers.
 * @param {string} queryFrom - The query parameter for filtering results by 'from' value.
 * @param {string} tabFilter - The query parameter for filtering results by 'tab' value.
 * @returns {Promise<{ success: boolean } | string[]>} - A promise that resolves to an object with a success flag
 * set to true, or an array of error messages.
 */
export const getSupplierSelectList = async (
  token,
  setSuppliers,
  queryFrom,
  tabFilter
) => {
  let URL = `${BACKEND_URL}suppliers/serve_supplier_select_list/`;
  // Try to send a GET request to retrieve the supplier select list

  if (queryFrom) URL += `?from=${queryFrom}`;
  if (tabFilter) URL += `&tab=${tabFilter}`;

  try {
    const response = await axios.get(URL, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    // If the request is successful, enhance supplier labels with inner_supplier_id
    const suppliersList = response.data.suppliers_list.map(supplier => {
      // If inner_supplier_id exists, append it to the label for better identification
      if (supplier.inner_supplier_id) {
        return {
          ...supplier,
          label: `${supplier.label} — Inner ID: ${supplier.inner_supplier_id}`,
        };
      }
      return supplier;
    });

    setSuppliers(suppliersList);

    // Return an object with success set to true
    return { success: true };

    // If any error occurs during the retrieval of the supplier select list
  } catch (error) {
    logError('supplier_client', 'getSupplierSelectList failed', error.response?.data ?? error);
    if (error.response?.data) {
      return Object.values(error.response.data);
    }
    return [error.message];
  }
};

/**
 * Retrieves the details of a specific supplier.
 *
 * This function sends a GET request to the backend API to retrieve the details of a specific supplier using the supplierId.
 * The function uses the provided setSupplierDetails callback function to update the supplier details state with the returned supplier data.
 * If the request is successful, it returns an object with a 'success' key. If any step fails, it returns an array of error messages.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} supplierId - The ID of the supplier to retrieve details for.
 * @param {function} setSupplierDetails - The function to set supplier details state.
 * @returns {Promise<Object>} - An object with 'success' key for successful actions, and array of error messages for unsuccessful ones.
 * @throws Will throw an error if the get supplier details request fails.
 */
export const getSupplierDetails = async (
  token,
  supplierId,
  setSupplierDetails
) => {
  // Try to send a GET request to retrieve the supplier details
  try {
    const response = await axios.get(`${BACKEND_URL}suppliers/${supplierId}/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    // If the request is successful, use the response to update the state of supplier details
    setSupplierDetails(response.data);

    // Return an object with success set to true
    return { success: true, supplierData: response.data };

    // If any error occurs during the retrieval of the supplier details
  } catch (error) {
    logError('supplier_client', 'getSupplierDetails failed', error.response?.data ?? error);
    if (error.response?.data) {
      return Object.values(error.response.data);
    }
    return [error.message];
  }
};

/**
 * Checks if a supplier's email is unique.
 *
 * This function sends a GET request to the backend API with a query parameter for the email value to check its uniqueness.
 * If the request is successful, it returns a boolean indicating if the email is unique or not.
 * If any step fails, it returns an array of error messages.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} value - The email value to check its uniqueness.
 * @returns {Promise<boolean | string[]>} - A boolean indicating if the email is unique or not, or an array of error messages if the request fails.
 * @throws Will throw an error if the check supplier email request fails.
 */
export const checkSupplierEmail = async (token, value) => {
  // Try to send a GET request to check if a supplier's email is unique

  try {
    const response = await axios.get(
      `${BACKEND_URL}suppliers/check_email/?value=${value}`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    // If the request is successful, return the uniqueness check result
    return response.data.unique;

    // If any error occurs during the email uniqueness check
  } catch (error) {
    logError('supplier_client', 'checkSupplierEmail failed', error.response?.data ?? error);
    if (error.response?.data) {
      return Object.values(error.response.data);
    }
    return [error.message];
  }
};

/**
 * Checks if a supplier's phone number is unique.
 *
 * This function sends a GET request to the backend API with query parameters for the phone number's prefix and suffix to check its uniqueness.
 * If the request is successful, it returns a boolean indicating if the phone number is unique or not.
 * If any step fails, it returns an array of error messages.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} prefix - The prefix of the phone number to check its uniqueness.
 * @param {string} suffix - The suffix of the phone number to check its uniqueness.
 * @returns {Promise<boolean | string[]>} - A boolean indicating if the phone number is unique or not, or an array of error messages if the request fails.
 * @throws Will throw an error if the check supplier phone request fails.
 */
export const checkSupplierPhone = async (token, prefix, suffix) => {
  // Try to send a GET request to check if a supplier's phone number is unique
  try {
    const response = await axios.get(
      `${BACKEND_URL}suppliers/check_phone/?prefix=${prefix}&suffix=${suffix}`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    // If the request is successful, return the uniqueness check result
    return response.data.unique;

    // If any error occurs during the phone number uniqueness check
  } catch (error) {
    logError('supplier_client', 'checkSupplierPhone failed', error.response?.data ?? error);
    if (error.response?.data) {
      return Object.values(error.response.data);
    }
    return [error.message];
  }
};

/**
 * Checks if a supplier's name is unique.
 *
 * This function sends a GET request to the backend API with a query parameter for the name to check its uniqueness.
 * If the request is successful, it returns a boolean indicating if the name is unique or not.
 * If any step fails, it returns an array of error messages.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} name - The name to check its uniqueness.
 * @returns {Promise<boolean | string[]>} - A boolean indicating if the name is unique or not, or an array of error messages if the request fails.
 * @throws Will throw an error if the check supplier name request fails.
 */
export const checkSupplierName = async (token, name) => {
  // Try to send a GET request to check if a supplier's name is unique
  try {
    const response = await axios.get(
      `${BACKEND_URL}suppliers/check_name/?name=${name}`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    // If the request is successful, return the uniqueness check result
    return response.data.unique;

    // If any error occurs during the name uniqueness check
  } catch (error) {
    logError('supplier_client', 'checkSupplierName failed', error.response?.data ?? error);
    if (error.response?.data) {
      return Object.values(error.response.data);
    }
    return [error.message];
  }
};

export const checkSupplierSecondaryEmail = async (token, value) => {
  // Try to send a GET request to check if a supplier's secondary email is unique
  try {
    const response = await axios.get(
      `${BACKEND_URL}suppliers/check_secondary_email/?value=${value}`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    // If the request is successful, return the uniqueness check result
    return response.data.unique;

    // If any error occurs during the secondary email uniqueness check
  } catch (error) {
    logError('supplier_client', 'checkSupplierSecondaryEmail failed', error.response?.data ?? error);
    if (error.response?.data) {
      return Object.values(error.response.data);
    }
    return [error.message];
  }
};

/**
 * Get detailed supplier information with lazy loading.
 *
 * This function fetches full supplier details including products, manufacturers, and secondary emails.
 * It uses client-side caching to improve performance and reduce API calls.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} supplierId - The ID of the supplier to fetch details for.
 * @returns {Promise<Object>} - The detailed supplier data.
 * @throws Will throw an error if the request fails.
 */
export const getSupplierDetailsLazy = async (token, supplierId) => {
  try {
    // Check cache first
    const cached = SupplierCache.get(supplierId);
    if (cached) {
      return cached;
    }

    // Send a GET request to the get_details endpoint
    const response = await axios.get(
      `${BACKEND_URL}suppliers/${supplierId}/get_details/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    // Cache the result
    SupplierCache.set(supplierId, response.data);

    return response.data;
  } catch (error) {
    logError('supplier_client', 'getSupplierDetailsLazy failed', error);
    throw error;
  }
};

/**
 * Get only products for a supplier.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} supplierId - The ID of the supplier to fetch products for.
 * @returns {Promise<Array>} - Array of products.
 * @throws Will throw an error if the request fails.
 */
export const getSupplierProducts = async (token, supplierId) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}suppliers/${supplierId}/get_products/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    logError('supplier_client', 'getSupplierProducts failed', error);
    throw error;
  }
};

/**
 * Get only manufacturers for a supplier.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} supplierId - The ID of the supplier to fetch manufacturers for.
 * @returns {Promise<Array>} - Array of manufacturers.
 * @throws Will throw an error if the request fails.
 */
export const getSupplierManufacturers = async (token, supplierId) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}suppliers/${supplierId}/get_manufacturers/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    logError('supplier_client', 'getSupplierManufacturers failed', error);
    throw error;
  }
};

/**
 * Get only secondary emails for a supplier.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} supplierId - The ID of the supplier to fetch secondary emails for.
 * @returns {Promise<Array>} - Array of secondary emails.
 * @throws Will throw an error if the request fails.
 */
export const getSupplierEmails = async (token, supplierId) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}suppliers/${supplierId}/get_emails/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    logError('supplier_client', 'getSupplierEmails failed', error);
    throw error;
  }
};
